/**
 * App-side wiring for `@heidi/calls/data`.
 *
 * Calls is the first domain whose seam exists for TRANSPORT rather than for
 * state. Four of its five api modules need nothing here — they read the app's
 * `scribeHttpV2` out of the `@heidi/api/client` registry (E5), which
 * `scribeHTTP.ts` populates at factory time. The fifth, `api/agent-chat.ts`,
 * cannot use that registry at all: it streams SSE and it talks to a separate
 * chat service addressed by an env var. So this module supplies the three
 * app-owned inputs those five endpoints need, and the endpoints themselves
 * live in the package.
 *
 * All three are THUNKS, called per request rather than resolved here. Each has
 * its own reason and none of them is style: the base URL reads the regional
 * domain, which can change after bootstrap; the stream headers read the
 * auth-token cache, which rotates; and the SSE client reads the domain again
 * on every stream. A value captured at wiring time would freeze whichever
 * answer happened to be true when `_app.tsx` first rendered.
 *
 * Imported at module scope in `_app.tsx` after the workspace wiring, which is
 * what populates the workspace-id the stream headers read.
 */
// Side-effect: builds the scribe clients AND sets the @heidi/api registry.
import '@/utils/scribeHTTP';

import { getDomains } from '@/helpers/getDomains';
import { customFetchEventSource } from '@/utils/fetchEventSource';
import { getV2StreamHeaders } from '@/utils/http/scribeSseHeaders';
import { baseV2URL } from '@/utils/scribeHTTP';
import { configureCallsData } from '@heidi/calls/data';

/**
 * Where the calls chat service lives, resolved fresh on every request.
 *
 * Transcribed from the hub body that used to sit beside the three fns it
 * serves. It stays app-side because every branch of it is an app read — two
 * `process.env` vars a package may not touch, and the regional domain
 * override.
 */
const resolveCallsChatApiBaseUrl = (): string => {
  if (process.env.NEXT_PUBLIC_CALLS_API_BASE_URL) {
    return `${process.env.NEXT_PUBLIC_CALLS_API_BASE_URL.replace(/\/$/, '')}/chat`;
  }

  if (process.env.NEXT_PUBLIC_ENVIRONMENT === 'localhost') {
    return 'http://localhost:8001/api/v1/calls/chat';
  }

  const baseUrl = getDomains()?.baseV2Url ?? baseV2URL;
  return `${baseUrl}calls/chat`;
};

configureCallsData({
  getCallsChatApiBaseUrl: resolveCallsChatApiBaseUrl,
  // NOT a calls function and deliberately not carved with the quartet: it is
  // the generic v2 stream-header builder, shared with every other raw-fetch
  // streaming caller. The family record says as much in its own words; moving
  // it into the package would have taken app surface this family does not own.
  // It moved OUT of the endpoint hub instead, so this wiring does not become a
  // new importer of a module whose importer set only shrinks — `scribeAPIV2`
  // re-exports it name-intact for the callers that already had it.
  getStreamHeaders: getV2StreamHeaders,
  fetchEventSource: customFetchEventSource,
});
